import React, { useContext, useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import Leaflet from 'leaflet';
import 'leaflet.markercluster';
import { useMap } from 'react-leaflet';
import LeafletMapContext from '../LeafletMapContextProvider';
import { buildMarker, markerClickBehaviour } from '../../helpers/marker';
import { DEFAULT_ZOOM_ON_CLICK_LEVEL, MARKER_CLICK_FOCUS } from '../../constants';
import './style.scss';
var DEFAULT_CLUSTER_SIZES = {
  MEDIUM: 10,
  NORMAL: 25,
  LARGE: 50,
  HUGE: 100
};
var LeafletMarkerClusters = function LeafletMarkerClusters(props) {
  var map = useMap();
  var _useContext = useContext(LeafletMapContext),
    setSelectedMarker = _useContext.setSelectedMarker;
  var markers = props.markers,
    _props$moveToMarkerOn = props.moveToMarkerOnClick,
    moveToMarkerOnClick = _props$moveToMarkerOn === void 0 ? true : _props$moveToMarkerOn,
    _props$zoomOnClick = props.zoomOnClick,
    zoomOnClick = _props$zoomOnClick === void 0 ? false : _props$zoomOnClick,
    _props$zoomLevelOnCli = props.zoomLevelOnClick,
    zoomLevelOnClick = _props$zoomLevelOnCli === void 0 ? DEFAULT_ZOOM_ON_CLICK_LEVEL : _props$zoomLevelOnCli,
    _props$focusPadding = props.focusPadding,
    focusPadding = _props$focusPadding === void 0 ? {
      x: 0,
      y: 0
    } : _props$focusPadding,
    _props$markerClickFoc = props.markerClickFocus,
    markerClickFocus = _props$markerClickFoc === void 0 ? MARKER_CLICK_FOCUS.MARKER : _props$markerClickFoc,
    _props$onMarkerClick = props.onMarkerClick,
    onMarkerClick = _props$onMarkerClick === void 0 ? markerClickBehaviour : _props$onMarkerClick,
    _props$clusterGroupIc = props.clusterGroupIconConstructor,
    clusterGroupIconConstructor = _props$clusterGroupIc === void 0 ? function (cluster) {
      var size = 'small';
      if (cluster.getChildCount() > DEFAULT_CLUSTER_SIZES.HUGE) {
        size = 'huge';
      } else if (cluster.getChildCount() > DEFAULT_CLUSTER_SIZES.LARGE) {
        size = 'large';
      } else if (cluster.getChildCount() > DEFAULT_CLUSTER_SIZES.NORMAL) {
        size = 'normal';
      } else if (cluster.getChildCount() > DEFAULT_CLUSTER_SIZES.MEDIUM) {
        size = 'medium';
      }
      return Leaflet.divIcon({
        html: "<div><span>".concat(cluster.getChildCount(), "</span></div>"),
        className: "marker-cluster marker-cluster-".concat(size)
      });
    } : _props$clusterGroupIc,
    _props$removeOutsideV = props.removeOutsideVisibleBounds,
    removeOutsideVisibleBounds = _props$removeOutsideV === void 0 ? true : _props$removeOutsideV,
    _props$zoomToBoundsOn = props.zoomToBoundsOnClick,
    zoomToBoundsOnClick = _props$zoomToBoundsOn === void 0 ? true : _props$zoomToBoundsOn,
    _props$showCoverageOn = props.showCoverageOnHover,
    showCoverageOnHover = _props$showCoverageOn === void 0 ? false : _props$showCoverageOn,
    _props$spiderfy = props.spiderfy,
    spiderfy = _props$spiderfy === void 0 ? true : _props$spiderfy,
    _props$spiderfyDistan = props.spiderfyDistanceMultiplier,
    spiderfyDistanceMultiplier = _props$spiderfyDistan === void 0 ? 1 : _props$spiderfyDistan,
    spiderfyShapePositions = props.spiderfyShapePositions,
    _props$onMarkerCluste = props.onMarkerClusterClick,
    onMarkerClusterClick = _props$onMarkerCluste === void 0 ? function (leafletMap, event) {
      if (spiderfy && leafletMap.getZoom() >= leafletMap.getMaxZoom()) {
        event.propagatedFrom.spiderfy();
      } else {
        leafletMap.flyToBounds(event.propagatedFrom.getBounds());
      }
    } : _props$onMarkerCluste,
    _props$showPopup = props.showPopup,
    showPopup = _props$showPopup === void 0 ? true : _props$showPopup,
    _props$popupContentCo = props.popupContentConstructor,
    popupContentConstructor = _props$popupContentCo === void 0 ? function (marker) {
      var div = document.createElement('div');
      var root = createRoot(div);
      root.render( /*#__PURE__*/React.createElement("span", null, marker.label));
      return div;
    } : _props$popupContentCo;
  var markerClusterGroup = useMemo(function () {
    return Leaflet.markerClusterGroup({
      zoomToBoundsOnClick: zoomToBoundsOnClick,
      showCoverageOnHover: showCoverageOnHover,
      spiderfyOnMaxZoom: false,
      removeOutsideVisibleBounds: removeOutsideVisibleBounds,
      spiderfyDistanceMultiplier: spiderfyDistanceMultiplier,
      iconCreateFunction: clusterGroupIconConstructor,
      spiderfyShapePositions: spiderfyShapePositions
    });
  }, []);

  /**
   * Process all items in the array and place them on the map
   * @param results - The results to place on the map
   * @param leafletMap - The map which will recieve the markers
   */
  var renderMarkers = function renderMarkers(results, leafletMap) {
    var markersToRender = [];
    results.forEach(function (marker) {
      var markerBuildOptions = {
        showPopup: showPopup,
        popupContentConstructor: popupContentConstructor,
        contextSetter: setSelectedMarker,
        onClick: onMarkerClick,
        moveToMarkerOnClick: moveToMarkerOnClick,
        zoomOnClick: zoomOnClick,
        zoomLevelOnClick: zoomLevelOnClick,
        markerClickFocus: markerClickFocus,
        focusPadding: focusPadding
      };
      var markerToRender = buildMarker(marker, markerBuildOptions, leafletMap);
      if (markerToRender) {
        markersToRender.push(markerToRender);
      }
    });
    markerClusterGroup.clearLayers();
    markerClusterGroup.addLayers(markersToRender);
  };
  useEffect(function () {
    renderMarkers(markers, map);
  }, [markers]);
  useEffect(function () {
    markerClusterGroup.clearLayers();
    markerClusterGroup.on('clusterclick', function (event) {
      return onMarkerClusterClick(map, event);
    });
    map.addLayer(markerClusterGroup);
  }, [map]);
  return /*#__PURE__*/React.createElement("div", {
    className: "markerClusters"
  });
};
export default LeafletMarkerClusters;